import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S3Query } from "types";
import { formatDateSafe } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S3Query = {
  from_date: startOfYear,
  to_date: todayString,
};

const S3Report: React.FC = () => {
  const { fetchS3Hkd } = useReport();
  const [filters, setFilters] = useState<S3Query>(defaultFilters);
  const [submitted, setSubmitted] = useState<S3Query | null>(null);

  const handleFilterChange = useCallback((key: keyof S3Query, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS3Hkd.mutate(filters);
    },
    [fetchS3Hkd, filters],
  );

  const records = (fetchS3Hkd.data?.data ?? []) as any[];

  const totals = useMemo(
    () =>
      records.reduce(
        (acc, item) => {
          const amount = Number(item.amount ?? 0) || 0;
          acc.total_cost += amount;
          if (item.is_deductible) {
            acc.deductible_cost += amount;
          } else {
            acc.non_deductible_cost += amount;
          }
          return acc;
        },
        { total_cost: 0, deductible_cost: 0, non_deductible_cost: 0 },
      ),
    [records],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "Ngày hạch toán",
        field: "accounting_date",
        minWidth: 140,
        valueFormatter: (params: any) => formatDateSafe(params.value),
      },
      {
        headerName: "Chứng từ",
        field: "voucher_code",
        minWidth: 120,
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 240,
        flex: 1,
      },
      {
        headerName: "Loại chi phí",
        field: "cost_category",
        minWidth: 140,
        valueFormatter: (params: any) => {
          const categoryMap: Record<string, string> = {
            MATERIALS: "Nguyên liệu, vật liệu",
            LABOR: "Nhân công",
            DEPRECIATION: "Khấu hao TSCĐ",
            SERVICES: "Dịch vụ mua ngoài",
            INTEREST: "Lãi vay",
            OTHER: "Chi phí khác",
          };
          return categoryMap[params.value] || params.value;
        },
      },
      {
        headerName: "Yếu tố chi phí",
        field: "cost_element",
        minWidth: 160,
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
      {
        headerName: "Được trừ",
        field: "is_deductible",
        minWidth: 100,
        valueFormatter: (params: any) => (params.value ? "Có" : "Không"),
        cellClass: (params: any) =>
          params.value ? "text-green-700" : "text-gray-500",
      },
      {
        headerName: "Có hóa đơn",
        field: "has_invoice",
        minWidth: 110,
        valueFormatter: (params: any) => (params.value ? "Có" : "Không"),
      },
      {
        headerName: "Thanh toán",
        field: "payment_method",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value === "CASH"
            ? "Tiền mặt"
            : params.value === "BANK"
              ? "Ngân hàng"
              : "",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS3Hkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Báo cáo S3-HKD</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            Sổ chi phí sản xuất, kinh doanh
          </h1>
          <p className="text-sm text-gray-600">
            Theo dõi chi phí sản xuất và kinh doanh theo thời gian.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? "Đang tải..." : "Xem báo cáo"}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <div className="flex flex-wrap items-center gap-6 text-right">
              <div>
                <p className="text-xs text-gray-500">Tổng chi phí</p>
                <p className="text-lg font-semibold text-gray-900">
                  {totals.total_cost.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Được trừ</p>
                <p className="text-lg font-semibold text-green-700">
                  {totals.deductible_cost.toLocaleString("vi-VN")} ₫
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Không được trừ</p>
                <p className="text-lg font-semibold text-red-700">
                  {totals.non_deductible_cost.toLocaleString("vi-VN")} ₫
                </p>
              </div>
            </div>
          </div>
          <AgGridTable
            rows={records}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S3Report;
